<template>
  <bar-chart
    v-if="chartData"
    :key="chartHeight"
    :data="chartData"
    :options="chartOptions"
    :plugins="chartPlugins"
    :height="chartHeight"
  />
  <div
    v-else
    class="h-32 flex items-center justify-center"
  >
    <span class="text-grey-001">No Data Found</span>
  </div>
</template>

<script>
import BarChart from "@/components/barChart";
import {tooltipConfig} from "@/utils/chartUtils";
import {handleValidateAndFormatNumber} from "@/utils/inputFormatter";

export default {
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  components: {
    BarChart
  },
  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val.length) {
          const baseline = val[0].avg_target_cycle_time_days
          this.chartData = {
            labels: this.data.map((item) => item.owner_name),
            datasets: [
              {
                type: 'line',
                data:this.data.map((item, index) => ({
                  x: baseline,
                  y: index === 0
                    ? -1
                    : index === this.data.length - 1
                      ? index + 1
                      : index
                })),
                borderColor: '#EE443F',
                borderWidth: 2,
                borderDash: [5, 5],
                pointRadius: 0,
                pointBorderWidth: 0,
                fill: false,
                parsing: false,
                spanGaps: true,
                pointHoverRadius: 0,
                xAxisID: 'x',
                yAxisID: 'y-base-line',
                pointHitRadius: 30
              },
              {
                data: this.data.map((item) => ({
                  owner_name: item.owner_name,
                  value: item.avg_actual_cycle_time_days
                })),
                borderWidth: 1,
                borderRadius: 5,
                backgroundColor: '#7451D0'
              }
            ]
          }

          const baseHeight = 120
          const barThickness = 30
          const padding = val.length > 5 ? 30 : 25
          this.chartHeight = Math.min(500, baseHeight + (val.length * barThickness) + padding)
        } else {
          this.chartData = null
        }
      }
    }
  },
  computed: {
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        barThickness: 25,
        indexAxis: 'y',
        parsing: {
          xAxisKey: 'value',
          yAxisKey: 'owner_name'
        },
        scales: {
          x: {
            type: 'logarithmic',
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          y: {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              callback(value, index) {
                return `${index + 1}`;
              }
            }
          },
          'x-base-line': {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          'y-base-line': {
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: tooltipConfig({
            mode: 'nearest',
            intersect: true,
            position: 'nearest',
            displayColors: false,
            callbacks: {
              title(tooltipItem) {
                const { datasetIndex, label } = tooltipItem[0]
                if (datasetIndex === 0) {
                  return 'Target'
                }
                return label
              },
              label(tooltipItem) {
                if (tooltipItem.datasetIndex === 0) {
                  return `${handleValidateAndFormatNumber(tooltipItem?.raw?.x)} days`
                }
                const { value } = tooltipItem?.raw
                const days = handleValidateAndFormatNumber(value)
                return `Time: ${days} days`
              }
            }
          })
        }
      }
    },
    chartPlugins() {
      return [
        this.drawBarValuePlugin(),
        this.drawBarLabelPlugin()
      ]
    }
  },
  data() {
    return {
      chartData: null,
      chartHeight: null
    }
  },
  methods: {
    drawBarValuePlugin() {
      return {
        id: 'drawBarValue',
        afterDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(1);

          meta.data.forEach((arc, index) => {
            const value = handleValidateAndFormatNumber(data.datasets[1].data[index].value)

            const {x, y, width} = arc; // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = 'normal 12px Poppins';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(value, x - width + 10, y);
            ctx.restore();
          });
        }
      }
    },
    drawBarLabelPlugin() {
      return {
        id: 'drawBarLabel',
        beforeDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const meta = chart.getDatasetMeta(1);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[1].data[index].owner_name;
            const {x, y, width, height, base} = arc

            ctx.save();
            ctx.fillStyle = '#000';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(value, base, y + height / 2 + 7);
            ctx.restore();
          });
        }
      }
    }
  }
}
</script>

<style lang="scss"  scoped>
</style>